import {
  CSSProperties,
  ReactNode,
  Ref,
  forwardRef,
  useState,
  useMemo,
  useRef,
  isValidElement,
  cloneElement,
  ReactElement,
} from 'react';
import classNames from 'classnames';
import {
  autoUpdate,
  offset,
  flip,
  shift,
  useFloating,
  useId,
  useHover,
  useClick,
  useFocus,
  useDismiss,
  useInteractions,
  useMergeRefs,
  useDelayGroup,
  useTransitionStyles,
  FloatingPortal,
  FloatingArrow,
  arrow,
  hide,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';

import { useOverlayContainer } from '../overlay/overlay-context';

import styles, {
  tooltipBorder,
  tooltipBorderLight,
  tooltipBorderError,
  tooltipBg,
  tooltipBgLight,
  tooltipBgError,
} from './tooltip.module.scss';

const tooltipBorders = {
  default: tooltipBorder,
  light: tooltipBorderLight,
  error: tooltipBorderError,
};

const tooltipBgs = {
  default: tooltipBg,
  light: tooltipBgLight,
  error: tooltipBgError,
};

interface TooltipBaseProps {
  size?: 'default' | 'compact';
  color?: 'default' | 'light' | 'error';
  title?: ReactNode;
  className?: classNames.Argument;
  children?: ReactNode;
  style?: CSSProperties;
}

const DefaultTooltipDelay = { open: 250, close: 0 };
const ArrowHeight = 8;
const ArrowGap = 4;

/**
 * @deprecated
 * Use the Tooltip component instead or its styles
 */
export const TooltipBase = forwardRef(function TooltipBase(
  { size = 'default', color = 'default', title, className, children, style }: TooltipBaseProps,
  ref: Ref<HTMLDivElement>,
) {
  return (
    <div
      className={classNames(styles.tooltipBase, className, {
        [styles.sizeDefault]: size === 'default',
        [styles.sizeCompact]: size === 'compact',
        [styles.colorDefault]: color === 'default',
        [styles.colorLight]: color === 'light',
        [styles.colorError]: color === 'error',
      })}
      style={style}
      ref={ref}>
      {title !== undefined && <span className={styles.tooltipTitle}>{title}</span>}
      {children}
    </div>
  );
});

type Delay = number | Partial<{ open: number; close: number }>;

interface TooltipProps {
  content: React.ReactNode | undefined;
  placement?: Placement;
  delay?: Delay;
  children: ReactElement<any>;
  trigger?: 'hover' | 'click' | null;
  hasArrow?: boolean;
  color?: 'default' | 'light' | 'error';
  open?: boolean;
  hideIfReferenceHidden?: boolean;
  enabled?: boolean;
}

export const Tooltip = forwardRef<HTMLElement, TooltipProps>(function Tooltip(
  {
    children,
    content,
    placement = 'top',
    trigger = 'hover',
    delay = DefaultTooltipDelay,
    hasArrow = true,
    color = 'default',
    open: forcedOpen = false,
    hideIfReferenceHidden = false,
    enabled = true,
  },
  propRef,
) {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef(null);
  const overlayContainer = useOverlayContainer();

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    placement,
    open: enabled && (open || forcedOpen),
    onOpenChange: setOpen,
    middleware: [
      offset(hasArrow ? ArrowHeight + ArrowGap : 0),
      shift({ padding: 8 }),
      flip({ fallbackAxisSideDirection: 'start' }),
      // eslint-disable-next-line react-compiler/react-compiler
      arrow({ element: arrowRef }), // Order matters: arrow middleware has to calculate position after offset, shift, flip
      hide(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const { delay: groupDelay, currentId, isInstantPhase } = useDelayGroup(context);

  const hover = useHover(context, {
    delay: groupDelay === 0 ? delay : groupDelay,
    move: false,
    enabled: trigger === 'hover' && enabled,
    mouseOnly: true, // Disable touch events for mobile
  });
  const click = useClick(context, { enabled: trigger === 'click' });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const mergedRefs = useMergeRefs(
    // eslint-disable-next-line react-compiler/react-compiler
    [
      refs.setReference,
      propRef,
      isValidElement(children) && typeof children.type !== 'string' ? (children as any).ref : null,
    ].filter(Boolean),
  );
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, click, focus, dismiss]);

  const tooltipId = useId();
  const tooltipProps = useMemo(() => getFloatingProps(), [getFloatingProps]);
  const triggerProps = useMemo(
    () =>
      getReferenceProps({
        ref: mergedRefs,
        'aria-labelledby': tooltipId,
        ...(children?.props !== undefined ? children.props : {}),
      }),
    [getReferenceProps, mergedRefs, tooltipId, children.props],
  );

  const instantDuration = 0;
  const openDuration = 250;
  const closeDuration = 250;

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: isInstantPhase
      ? {
          open: instantDuration,
          close: currentId === context.floatingId ? closeDuration : instantDuration,
        }
      : {
          open: openDuration,
          close: closeDuration,
        },
    initial: {
      opacity: 0,
      scale: '0.925',
    },
    common: ({ side }) => ({
      transitionTimingFunction: 'cubic-bezier(.18,.87,.4,.97)',
      transformOrigin: {
        top: 'bottom',
        left: 'right',
        bottom: 'top',
        right: 'left',
      }[String(side)],
    }),
  });

  if (content === undefined || content === '') {
    return children;
  }

  return (
    <>
      {isValidElement(children) && cloneElement(children, triggerProps)}
      {isMounted && (
        <FloatingPortal root={overlayContainer}>
          <div role="tooltip" ref={refs.setFloating} style={floatingStyles}>
            <div
              className={classNames(styles.tooltip, {
                [styles.colorDefault]: color === 'default',
                [styles.colorLight]: color === 'light',
                [styles.colorError]: color === 'error',
              })}
              style={{
                ...transitionStyles,
                visibility:
                  hideIfReferenceHidden && middlewareData.hide?.referenceHidden === true
                    ? 'hidden'
                    : undefined,
              }}
              {...tooltipProps}>
              {content}

              {hasArrow && (
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  tipRadius={1}
                  height={ArrowHeight}
                  fill={tooltipBgs[color]}
                  stroke={tooltipBorders[color]}
                  strokeWidth={1}
                />
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
});
